import type {ReactNode} from 'react';
import type {FormatXMLElementFn} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import {useSelector} from 'src/v2/state/store';
import {Link} from 'src/v2/components/common/Link';
import {useContactSupportUrl} from 'src/v2/util/useContactSupportUrl';
import {REFUNDABLE_REASONS} from 'src/v2/components/anyCrypto/relayConstants';

const messages = {
  failedSubtitle: message({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle',
    description: 'Subtitle when relay payment has failed with unknown reason',
    defaultMessage:
      'This is usually due to insufficient funds or a network issue. Any funds deducted will be refunded. Start a new payment to try again.',
  }),
  underpayment: message<{
    b: FormatXMLElementFn;
    expectedAmount: React.ReactNode;
    expectedToken: React.ReactNode;
    refundDestination: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_underpayment',
    description:
      'Subtitle when relay payment failed due to insufficient deposit amount',
    defaultMessage:
      "An insufficient amount was deposited: expected <b>{expectedAmount} {expectedToken}</b>. We'll refund your payment to {refundDestination}—try again.",
  }),
  wrongToken: message<{
    b: FormatXMLElementFn;
    expectedToken: React.ReactNode;
    refundDestination: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_wrong_token',
    description:
      'Subtitle when relay payment failed due to wrong token (no details)',
    defaultMessage:
      "A deposit was made with the wrong token: expected <b>{expectedToken}</b>. We'll refund your payment to {refundDestination}—try again.",
  }),
  wrongTokenWithReceived: message<{
    b: FormatXMLElementFn;
    expectedToken: React.ReactNode;
    receivedToken: React.ReactNode;
    refundDestination: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_wrong_token_with_received',
    description:
      'Subtitle when relay payment failed due to wrong token (with received details)',
    defaultMessage:
      "A deposit was made with the wrong token: expected <b>{expectedToken}</b> (received <b>{receivedToken}</b>). We'll refund your payment to {refundDestination}—try again.",
  }),
  wrongNetwork: message<{
    b: FormatXMLElementFn;
    expectedNetwork: React.ReactNode;
    refundDestination: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_wrong_network',
    description:
      'Subtitle when relay payment failed due to wrong network (no details)',
    defaultMessage:
      "A deposit was made with the wrong network: expected on <b>{expectedNetwork}</b>. We'll refund your payment to {refundDestination}—try again.",
  }),
  wrongNetworkWithReceived: message<{
    b: FormatXMLElementFn;
    expectedNetwork: React.ReactNode;
    receivedNetwork: React.ReactNode;
    refundDestination: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_wrong_network_with_received',
    description:
      'Subtitle when relay payment failed due to wrong network (with received details)',
    defaultMessage:
      "A deposit was made with the wrong network: expected on <b>{expectedNetwork}</b> (received on <b>{receivedNetwork}</b>). We'll refund your payment to {refundDestination}—try again.",
  }),
  vasp: message<{
    supportLink: FormatXMLElementFn;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle_vasp',
    description:
      'Additional VASP sentence when refund cannot be issued automatically',
    defaultMessage:
      "We're unable to automatically issue a refund because the payment may have been sent from an exchange without a specified refund address. <supportLink>Contact Stripe support</supportLink> to provide a refund address.",
  }),
};

function getRefundDestination(customRefundAddress: string) {
  if (customRefundAddress) {
    return (
      <b>
        {customRefundAddress.slice(0, 6)}...{customRefundAddress.slice(-4)}
      </b>
    );
  }
  return 'the address you sent payment from';
}

export const useGetFailureSubtitle = (): ReactNode | null => {
  const intl = useIntl();
  const relayFailReason = useSelector(
    (state) => state.anyCryptoDeposit.relayFailReason,
  );
  const relayFailureDetails = useSelector(
    (state) => state.anyCryptoDeposit.relayFailureDetails,
  );
  const relayRefundFailReason = useSelector(
    (state) => state.anyCryptoDeposit.relayRefundFailReason,
  );
  const customRefundAddress = useSelector(
    (state) => state.anyCryptoDeposit.customRefundAddress,
  );
  const quote = useSelector((state) => state.anyCryptoDeposit.quote);
  const contactSupportUrl = useContactSupportUrl();

  const isRefundBlockedBecauseVasp =
    relayRefundFailReason === 'REFUND_RECIPIENT_IS_VASP';
  const isKnownReason = REFUNDABLE_REASONS.has(relayFailReason ?? '');

  if (!isKnownReason) {
    return intl.formatMessage(messages.failedSubtitle);
  }

  const b = (chunks: ReactNode) => <b>{chunks}</b>;
  const refundDestination = getRefundDestination(customRefundAddress);
  const hasDetails = relayFailureDetails !== null;

  const expectedAmount =
    relayFailureDetails?.expected_minimum_amount ?? quote.inputAmount ?? '';
  const expectedToken =
    relayFailureDetails?.expected_token ??
    quote.inputCurrency?.toUpperCase() ??
    '';
  const expectedNetwork =
    relayFailureDetails?.expected_chain ?? quote.inputChain ?? '';

  let errorSentence: ReactNode;
  switch (relayFailReason) {
    case 'DEPOSITED_AMOUNT_TOO_LOW_TO_FILL':
      errorSentence = intl.formatMessage(messages.underpayment, {
        b,
        expectedAmount,
        expectedToken,
        refundDestination,
      });
      break;
    case 'ORIGIN_CURRENCY_MISMATCH':
      errorSentence =
        hasDetails && relayFailureDetails?.deposit_token
          ? intl.formatMessage(messages.wrongTokenWithReceived, {
              b,
              expectedToken,
              receivedToken: relayFailureDetails.deposit_token,
              refundDestination,
            })
          : intl.formatMessage(messages.wrongToken, {
              b,
              expectedToken,
              refundDestination,
            });
      break;
    case 'DEPOSIT_CHAIN_MISMATCH':
      errorSentence =
        hasDetails && relayFailureDetails?.deposit_chain
          ? intl.formatMessage(messages.wrongNetworkWithReceived, {
              b,
              expectedNetwork,
              receivedNetwork: relayFailureDetails.deposit_chain,
              refundDestination,
            })
          : intl.formatMessage(messages.wrongNetwork, {
              b,
              expectedNetwork,
              refundDestination,
            });
      break;
    default:
      return intl.formatMessage(messages.failedSubtitle);
  }

  if (isRefundBlockedBecauseVasp) {
    return (
      <>
        {errorSentence}{' '}
        {intl.formatMessage(messages.vasp, {
          supportLink: (chunks: ReactNode) => (
            <Link type="secondary" href={contactSupportUrl} target="_blank">
              {chunks}
            </Link>
          ),
        })}
      </>
    );
  }

  return errorSentence;
};
